import { cn } from '@/lib/utils';
import type { ProntuarioPatientHeader } from '../../utils/resolveProntuarioPatientHeader';

type ProntuarioProfileVariant = 'dark' | 'light';

const PROFILE_THEME = {
	dark: {
		name: 'max-w-full truncate text-[22px] font-medium leading-tight tracking-tight text-slate-100',
		meta: 'text-[14px] leading-relaxed text-slate-500',
		avatar: 'flex shrink-0 items-center justify-center rounded-full bg-[#8ab4f8]/12 font-medium uppercase tracking-wide text-[#8ab4f8] ring-2 ring-[#8ab4f8]/30',
		chip: 'inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-[#1a2430] px-3 py-1.5 text-[13px] font-medium leading-none text-slate-200',
		chipLabel: 'text-slate-500',
		tag: 'inline-flex rounded-md bg-white/[0.06] px-2 py-0.5 text-[12px] font-medium leading-none text-slate-400',
	},
	light: {
		name: 'max-w-full truncate text-[22px] font-semibold leading-tight tracking-tight text-zinc-950',
		meta: 'text-[14px] leading-relaxed text-zinc-600',
		avatar: 'flex shrink-0 items-center justify-center rounded-full bg-blue-100 font-semibold uppercase tracking-wide text-blue-700 ring-2 ring-blue-200',
		chip: 'inline-flex items-center gap-1 rounded-full border border-zinc-300 bg-zinc-100 px-3 py-1.5 text-[13px] font-medium leading-none text-zinc-800',
		chipLabel: 'text-zinc-600',
		tag: 'inline-flex rounded-md bg-zinc-200 px-2 py-0.5 text-[12px] font-medium leading-none text-zinc-700',
	},
} as const satisfies Record<ProntuarioProfileVariant, Record<string, string>>;

const formatTotalSessionsLabel = (sessionNumber: string): string => {
	const total = Number.parseInt(sessionNumber.trim(), 10);
	if (Number.isNaN(total) || total < 1) {
		return `${sessionNumber.trim() || '—'} sessões totais`;
	}
	return total === 1 ? '1 sessão total' : `${total} sessões totais`;
};

const joinMetaParts = (...parts: Array<string | null | undefined>): string =>
	parts
		.map((part) => part?.trim())
		.filter((part): part is string => Boolean(part))
		.join(' · ');

type SessionCallProntuarioPatientProfileHeroProps = {
	header: ProntuarioPatientHeader;
	cpf?: string | null;
	gender?: string | null;
	className?: string;
	variant?: ProntuarioProfileVariant;
};

const SessionCallProntuarioPatientProfileHero = ({
	header,
	cpf,
	gender,
	className,
	variant = 'dark',
}: SessionCallProntuarioPatientProfileHeroProps) => {
	const theme = PROFILE_THEME[variant];
	const identityMetaLine = joinMetaParts(cpf, gender);

	return (
		<header className={cn('min-w-0', className)}>
			<div className="flex flex-col">
				<div className="flex items-center gap-4">
					<div
						className={cn(theme.avatar, 'size-16 text-xl')}
						aria-hidden
					>
						{header.initials}
					</div>

					<div className="min-w-0 flex-1">
						<h1 className={theme.name} title={header.fullName}>
							{header.fullName}
						</h1>

						<p className={cn('mt-1', theme.meta)}>
							{joinMetaParts(
								header.ageLabel,
								formatTotalSessionsLabel(header.sessionNumber),
							)}
						</p>
						{identityMetaLine ? (
							<p className={cn('mt-0.5', theme.meta)}>
								{identityMetaLine}
							</p>
						) : null}
					</div>
				</div>

				<div className="mt-3 flex flex-wrap gap-2">
					<span className={theme.chip}>
						<span className={theme.chipLabel}>Início</span>
						<span>{header.firstSessionDate}</span>
					</span>
					{header.frequency ? (
						<span className={theme.chip}>
							<span className={theme.chipLabel}>Frequência</span>
							<span>{header.frequency}</span>
						</span>
					) : null}
				</div>

				{header.tags.length > 0 ? (
					<div className="mt-2 flex flex-wrap gap-1.5">
						{header.tags.map((tag) => (
							<span key={tag} className={theme.tag}>
								{tag}
							</span>
						))}
					</div>
				) : null}
			</div>
		</header>
	);
};

export { SessionCallProntuarioPatientProfileHero };
